import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CopyToClipboard } from "../../Utils";
import { fetchVehicleInsurances } from "../../../api/vehicles/insurance";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";

export const InsuranceSection = ({ vehicleId, className = "" }) => {
  const { t } = useTranslation();
  const [insurance, setInsurance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (vehicleId) {
      fetchInsurance();
    }
  }, [vehicleId]);

  const fetchInsurance = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchVehicleInsurances({
        filters: [{ name: "vehicle_id", value: vehicleId }],
        per_page: 10,
        sortBy: "start_date",
        sort: "DESC",
      });
      const insurancesList = response?.data || [];
      const active = insurancesList.find((i) => i.active) || insurancesList[0] || null;
      setInsurance(active);
    } catch (err) {
      console.error("Error fetching insurance:", err);
      setError("Failed to load insurance data");
    } finally {
      setLoading(false);
    }
  };

  if (!vehicleId) {
    return null;
  }

  if (loading) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md mt-5 ${className}`}>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className="animate-pulse flex space-x-4 w-full">
              <div className="rounded-lg bg-blue-200 h-10 w-10"></div>
              <div className="flex-1 space-y-2 py-1">
                <div className="h-4 bg-blue-200 rounded w-1/4"></div>
                <div className="h-3 bg-blue-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white px-6 py-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md mt-5 ${className}`}>
        <div className="bg-red-50 px-6 py-4 border-b border-red-200">
          <div className="flex items-center">
            <div className="text-red-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-red-900">{t("Error Loading Insurance")}</h3>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!insurance) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md mt-5 ${className}`}>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <ShieldCheckIcon className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">{t("Insurance Information")}</h3>
                <p className="text-sm text-gray-600">{t("Current vehicle insurance details")}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white px-6 py-6">
          <div className="text-center py-4">
            <p className="text-sm text-gray-500">{t("No insurance information available")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`shadow sm:overflow-hidden sm:rounded-md mt-5 ${className}`}>
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ShieldCheckIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="ml-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900">{t("Insurance Information")}</h3>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    insurance.active
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {insurance.active ? t("Active") : t("Inactive")}
                </span>
              </div>
              <p className="text-sm text-gray-600">{t("Current vehicle insurance details")}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Insurance Company")}</dt>
            <dd className="mt-1 text-sm text-gray-900 flex items-center">
              {insurance.insurance_company || "-"}
              {insurance.insurance_company && (
                <CopyToClipboard value={insurance.insurance_company} />
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Policy Number")}</dt>
            <dd className="mt-1 text-sm text-gray-900 flex items-center">
              {insurance.policy_number || "-"}
              {insurance.policy_number && (
                <CopyToClipboard value={insurance.policy_number} />
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Premium (NOK)")}</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {insurance.premium_nok
                ? `${insurance.premium_nok.toLocaleString()} NOK`
                : "-"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Start Date")}</dt>
            <dd className="mt-1 text-sm text-gray-900">{insurance.start_date || "-"}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("End Date")}</dt>
            <dd className="mt-1 text-sm text-gray-900">{insurance.end_date || "-"}</dd>
          </div>
          {insurance.notes && (
            <div className="md:col-span-3">
              <dt className="text-sm font-medium text-gray-500">{t("Notes")}</dt>
              <dd className="mt-1 text-sm text-gray-900">{insurance.notes}</dd>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

